import {StemDate} from "../../stem-core/time/Date";
import {Money, MoneyObject} from "../../stem-core/localization/Money";
import {Wallet} from "./WalletStore";
import {TimeUnit} from "../../stem-core/time/Duration";
import {TopUpPayment} from "./TopUpPaymentStore";
import {BaseEnum, makeEnum} from "../../stem-core/state/BaseEnum";
import {field} from "../../stem-core/state/StoreField";
import {BaseStore, globalStore} from "../../stem-core/state/Store";
import {Merchant} from "./MerchantStore";
import {Subscription} from "./SubscriptionStore";
import {PaymentMethod} from "./PaymentMethodStore";
import {Donation} from "./DonationStore";
import {Purchase} from "./PurchaseStore";


// TODO: standardize enums
export const PaymentType = Object.freeze({
    PURCHASE: "purchase",
    SUBSCRIPTION: "subscription",
    DONATION: "donation",
});

// What the `comment` field holds once parsed
interface PaymentComment {
    title?: string;
    url?: string;
}

// TODO use this and uncomment @field(PaymentStatus)
@makeEnum
class PaymentStatus extends BaseEnum {
    static SUCCEEDED;
    static REVERTED;
}

@globalStore
export class Payment extends BaseStore("Payment", {dependencies: ["TopUpPayment", "PaymentMethod", "Wallet"]}, MoneyObject) {
    static API_LIST_ENDPOINT = "/payments/list_payments";

    static ResourceType = PaymentType;
    // TODO @cleanup make enum
    static Status = {
        SUCCEEDED: "succeeded",
        REVERTED: "reverted",
    };

    @field("Merchant") merchant: Merchant;
    @field("TopUpPayment") topUp: TopUpPayment;
    @field(Date) createdAt: StemDate;
    @field(Boolean) isAutomatic: boolean;
    //@field(PaymentStatus) status;  TODO not used right now since we need to go through usage
    @field(String) status: typeof Payment.Status[keyof typeof Payment.Status];
    @field(String) resourceType: typeof PaymentType[keyof typeof PaymentType];
    @field(String) resourceId: string;
    @field(Number) debitedAmount: number;
    @field(Boolean) isOneTime: boolean;
    @field(String) comment?: string;

    getMerchant(): Merchant {
        return this.merchant;
    }

    // The thing we're paying money for
    getResource(): Subscription | Donation | Purchase {
        // TODO @cleanup have way to get the store from the enum type and use that
        const {resourceType, resourceId} = this;
        if (resourceType === Payment.ResourceType.SUBSCRIPTION) {
            return this.getStore("Subscription").get(resourceId);
        }
        if (resourceType === Payment.ResourceType.PURCHASE) {
            return this.getStore("Purchase").get(resourceId);
        }
        if (resourceType === Payment.ResourceType.DONATION) {
            return this.getStore("Donation").get(resourceId);
        }
        console.error("Missing resource type", resourceType);
        return null;
    }

    getComment(): PaymentComment | null {
        return this.comment && JSON.parse(this.comment);
    }

    getTitle(): string {
        return this.getComment()?.title || "";
    }

    getUrl(): string {
        return this.getComment()?.url || "";
    }

    getDebitedAmount(): Money {
        // TODO: use this.debitedCurrencyId instead of wallet currency id when it is retrieved by the API
        const walletCurrency = Wallet.getWallet()?.getCurrency();
        return new Money(this.debitedAmount, walletCurrency);
    }

    getType(): string {
        return this.resourceType;
    }

    // TODO: rename this and refactor or remove
    // TODO: implement isRecurring() method
    getCategory(): string | null {
        if (this.getType() === PaymentType.DONATION) { // TODO: unify and standardize enums
            if (this.isOneTime) {
                return "One-time";
            } else {
                return "Recurring";
            }
        }
        return null;
    }

    // This is arbitrary chosen.
    isRefundable(): boolean {
        return this.isAutomatic && StemDate.now().diff(this.createdAt).valueOf() < TimeUnit.DAY.valueOf();
    }

    getTopUpPayment(): TopUpPayment | null {
        return TopUpPayment.get(this.topUpId);
    }

    // Return the payment method used by the top-up, if this payment triggered a top-up
    getPaymentMethod(): PaymentMethod | null {
        const topUpPayment = this.getTopUpPayment();
        if (topUpPayment) {
            return topUpPayment.getPaymentMethod();
        }
        if (this.resourceType === "donation" || this.resourceType === "subscription") {
            const resource = this.getStore(this.resourceType).get(this.resourceId);
            if (resource && resource.preferredPaymentMethodId) {
                return resource.getPaymentMethod();
            }
        }
        return null;
    }

    isRefunded(): boolean {
        return this.status === "reverted"; // TODO @branch Damn enums
    }
}
